import { useCallback, useMemo } from "react";

import debounce from "lodash/debounce";
import { useSelector } from "react-redux";

import useLatestValue from "app/custom-hooks/useLatestValue";
import { useUnmount } from "app/custom-hooks/useUnmount";
import type { IFilterManagerModel } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filters-manager/types";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import advancedSearchFilteringTrackingService from "../services/tracking";
import { searchPanelContextDict } from "../services/tracking/const";
import { selectIsFiltersCategoriesPanelExpanded } from "../store/selectors";

const SEARCH_TRACKING_DEBOUNCE_MS = 300;

type HookValue = {
    onTrackSearchFocus: VoidFunction;
    onTrackSearchChange: (query: string) => void;
};

export const useFilterSearchTracking = <T extends IPlainObject = IPlainObject>(
    filtersManager: IFilterManagerModel<T>,
): HookValue => {
    const isCategoryPanelExpanded = useSelector(selectIsFiltersCategoriesPanelExpanded);

    const panelContext = isCategoryPanelExpanded
        ? searchPanelContextDict.expandedAllTab
        : searchPanelContextDict.mainSidebar;

    const filtersManagerRef = useLatestValue(filtersManager);
    const panelContextRef = useLatestValue(panelContext);

    const debouncedTrackSearchType = useMemo(
        () =>
            debounce((charCount: number) => {
                const filterCount = filtersManagerRef.current.getAll().length;

                advancedSearchFilteringTrackingService.filtersPanel.trackSearchType(
                    charCount,
                    filterCount,
                    panelContextRef.current,
                );
            }, SEARCH_TRACKING_DEBOUNCE_MS),
        [],
    );

    useUnmount(() => {
        debouncedTrackSearchType.cancel();
    });

    const onTrackSearchFocus = useCallback(() => {
        advancedSearchFilteringTrackingService.filtersPanel.trackSearchInputFocused();
    }, []);

    const onTrackSearchChange = useCallback(
        (query: string) => {
            if (query.length === 0) {
                debouncedTrackSearchType.cancel();
                advancedSearchFilteringTrackingService.filtersPanel.trackSearchClear();

                return;
            }
            debouncedTrackSearchType(query.length);
        },
        [debouncedTrackSearchType],
    );

    return { onTrackSearchFocus, onTrackSearchChange };
};
